<template>
  <div>
    <ui-modal
      size="lg"
      :visible="!hasErrorOnLoadingImage"
      @on-close="handleCloseModal"
      ref="crop-image-ui-modal"
    >
      <template #title>
        <div class="modal__title--tab">
          <a class="active">{{ $i18nPresenter.t('Crop and resize') }}</a>
        </div>
      </template>

      <template #body>
        <div class="crop-content">
          <img
            ref="canvas"
            :src="imageUrl"
            class="invisible"
            crossOrigin="Anonymous"
            @error="handleImageLoadFailed"
            @load="handleImageLoadSucceeded"
          >
        </div>
        <Uploader v-if="isImageLoading" />
      </template>

      <template #footer>
        <div class="tab-range horizontal layout center center-justified">
          <button
            :disabled="isSubmitting"
            class="btn btn-light min-w-60px"
            @click="$emit('on-cancel')"
          >
            {{ $i18nPresenter.t('Back') }}
          </button>

          <span
            @click="cropper.zoom(-0.1)"
            class="ml-3 mr-3 font-size-xxxl pointer user-select-none"
          >
            -
          </span>

          <input
            step="any"
            type="range"
            :min="minScale"
            :max="maxScale"
            v-model="scale"
            class="custom-range flex"
            @input="handleRatioChange"
          >

          <span
            @click="cropper.zoom(0.1)"
            class="ml-3 mr-3 font-size-xxxl pointer user-select-none"
          >
            +
          </span>

          <button
            :disabled="isSubmitting"
            @click="handleSaveButtonClick"
            class="btn btn-primary min-w-80px"
            @mouseup.stop
          >
            <span class="mr-1">
              <i v-if="!isSubmitting" class="fas fa-check" />
              <span v-else class="loading-spinner" />
            </span>
            <span>{{ $i18nPresenter.t('Save') }}</span>
          </button>
        </div>
      </template>
    </ui-modal>

    <ModalUploadImageError
      v-if="hasErrorOnLoadingImage || hasErrorOnCroppingImage"
      @handleOkButtonClick="handleOkButtonClick()"
    />
  </div>
</template>

<script>
// node modules
import { mapState } from 'vuex'
import Cropper from 'cropperjs'
import * as Sentry from '@sentry/vue'

import Config from '@/config'
import Socket from '@/socket'
import Utils from '@/util'

// constants
import constants from '@/constant'

// UI
import Uploader from '@/components/UI/loaders/Uploader'
import Modal from '@/components/UI/modals/Modal'
import ModalUploadImageError from '@/components/UI/modals/UploadImageError.vue'

const { eventCategories } = constants

export default {
  components: {
    Uploader,
    ModalUploadImageError,
    UiModal: Modal,
  },
  props: {
    imageUrl: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: null,
    },
    aspectRatio: {
      type: Number,
      default: NaN,
    },
    cropperData: {
      type: Object,
      default: null,
    },
    initialAspectRatio: {
      type: Number,
      default: 16 / 9,
    },
    isCropForMetaThumbnail: {
      type: Boolean,
      default: false,
    },
    isChangingImage: {
      type: Boolean,
      default: false,
    },
    expires: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      ratio: null,
      scale: null,
      cropper: '',
      minScale: 0,
      maxScale: 100,
      maxRatio: 3,
      minRatio: 0.01,
      extension: null,
      isLoading: false,
      isSubmitting: false,
      isImageLoading: true,
      initialRatio: null,
      hasErrorOnLoadingImage: false,
      hasErrorOnCroppingImage: false,
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
    }),
  },
  watch: {

    ratio(ratio) {
      const midScale = (this.minScale + this.maxScale) / 2

      if (ratio <= this.initialRatio) {
        const ratioPerScale = (this.initialRatio - this.minRatio) / midScale
        this.scale = (ratio - this.minRatio) / ratioPerScale
        return
      }

      const ratioPerScale = (this.maxRatio - this.initialRatio) / midScale
      this.scale = midScale + (ratio - this.initialRatio) / ratioPerScale
    },
  },
  mounted() {
    if (!this.imageUrl) {
      this.$emit('on-cancel')
      return
    }

    // cannot edit gif file
    this.extension = this.imageUrl.split('?')[0].split('.').pop()
    if (this.extension === 'gif') {
      this.$emit('on-cancel')
      return
    }

    this.scale = (this.maxScale + this.minScale) / 2
  },
  methods: {
    handleImageLoadSucceeded() {
      const cropperOptions = {
        dragMode: 'move',
        minCropBoxWidth: 100,
        minCropBoxHeight: 100,
        cropBoxMovable: false,
        checkCrossOrigin: false,
        checkOrientation: false,
        aspectRatio: this.aspectRatio,
        initialAspectRatio: this.initialAspectRatio,
        ready: () => {
          this.isImageLoading = false

          if (this.cropperData) {
            const { canvasData, cropBoxData, initialRatio } = this.cropperData
            this.ratio = canvasData.width / canvasData.naturalWidth
            this.initialRatio = initialRatio || this.ratio
            this.cropper.setCanvasData(canvasData)
            this.cropper.setCropBoxData(cropBoxData)
            return
          }

          const imageData = this.cropper.getImageData()
          const cropBoxData = this.cropper.getCropBoxData()

          // make the image fits the crop box
          this.ratio = cropBoxData.width / imageData.naturalWidth
          this.initialRatio = this.ratio

          if (this.ratio < this.minRatio) this.ratio = this.minRatio
          if (this.ratio + 1 > this.maxRatio) {
            this.maxRatio = this.ratio + 1
          }

          this.cropper.zoomTo(this.ratio)
        },
        zoom: (event) => {
          const { ratio } = event.detail

          if (ratio < this.minRatio || ratio > this.maxRatio) {
            event.preventDefault()
            return
          }

          this.ratio = ratio
        },
      }

      this.cropper = new Cropper(this.$refs.canvas, cropperOptions)
    },
    getCroppedImage() {
      let quality = 1
      let extension = 'png'
      let fileType = 'image/png'
      let fillColor = 'transparent'
      let width = 1280
      let height = 720
      const imageW = this.cropper.getImageData().naturalWidth
      if (this.isCropForMetaThumbnail && imageW > 1200) {
        width = 1200
        height = 675
      } else if (imageW >= 2560) {
        width = 2560
        height = 1440
      } else if (imageW > 1280) {
        width = imageW
        height = parseInt((imageW / 16) * 9, 10)
      }
      // cropped image will be jpeg 60%
      if (this.extension === 'jpg' || this.extension === 'jpeg') {
        extension = 'jpg'
        fileType = 'image/jpeg'
        fillColor = this.slideActive.baseColour
        quality = 0.9
      }

      return new Promise((resolve) => {
        this.cropper
          .getCroppedCanvas({
            fillColor,
            height,
            width,
            imageSmoothingQuality: 'high',
          })
          .toBlob(async (blob) => {
            const file = new File([blob], `cropped-image.${extension}`, {
              type: fileType,
            })
            resolve(file)
          }, fileType, quality)
      })
    },
    convertScaleToRatio(scale) {
      const midScale = (this.minScale + this.maxScale) / 2

      if (scale <= midScale) {
        const ratioPerScale = (this.initialRatio - this.minRatio) / midScale
        return this.minRatio + scale * ratioPerScale
      }

      const ratioPerScale = (this.maxRatio - this.initialRatio) / midScale
      return this.initialRatio + ((scale - midScale) * ratioPerScale)
    },
    handleRatioChange() {
      const ratio = this.convertScaleToRatio(this.scale)
      this.cropper.zoomTo(ratio)
    },
    async handleSaveButtonClick() {
      if (this.isSubmitting) return

      this.isSubmitting = true

      const file = await this.getCroppedImage()
      const formData = new FormData()
      formData.append('file', file)
      formData.append('type', this.type)

      formData.append('socketId', Socket.getSocketId())
      formData.append('accessCode', this.$store.state.presentation.uniqueAccessCode)
      if (this.expires) {
        formData.append('expires', this.expires)
      }
      try {
        const res = await this.$http.post(Config.api.uploadImage, formData)

        const canvasData = this.cropper.getCanvasData()
        const cropBoxData = this.cropper.getCropBoxData()

        this.$emit('handleCropImageFinished', {
          raw: null,
          originImageUrl: this.imageUrl,
          crop: res.body.file.location,
          key: res.body.file.key,
          cropperData: {
            canvasData,
            cropBoxData,
            initialRatio: this.initialRatio,
          },
          isChangingImage: this.isChangingImage,
        })
      } catch (error) {
        Sentry.captureException(error)
        this.isSubmitting = false
        this.hasErrorOnCroppingImage = true
      }
    },
    handleCloseModal() {
      this.$emit('hideModal')
    },
    handleImageLoadFailed() {
      this.hasErrorOnLoadingImage = true
      Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Image > Upload > Failed')
    },
    handleOkButtonClick() {
      if (this.hasErrorOnLoadingImage) {
        this.$emit('on-cancel')
        return
      }

      if (this.hasErrorOnCroppingImage) {
        this.hasErrorOnCroppingImage = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/mixins.scss";
@import "@/assets/scss/variable.scss";

.tab-range {
  width: 100%;
}

.tab-btns {
  width: 80%;
  margin: 0 auto;
  cursor: pointer;
}

.crop-content {
  height: 350px;
  padding: 0;
  margin: 0;

  img {
    max-width: 100%;
  }
}

.loading-crop-image {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: #00000087;
  color: #fff;
}
</style>
